import { Children, ReactElement } from "react"
import styled, { PolymorphicComponentProps } from "styled-components"
import { StyledTarget } from "styled-components/dist/types"
import { ChevronRightIcon } from "../../icons/ChevronRight"
import { Typography } from "../../typography"

const MarginedChevron = styled(ChevronRightIcon)`
  margin: 0.15rem 0.5rem;
`

const Nav = styled.nav`
  display: inline-flex;
`

const NavList = styled.ol`
  display: flex;
  align-items: center;
`

const NavItem = styled.li`
  display: inline-flex;
  align-items: center;
`

export const DeprecatedBreadcrumbItem = styled(Typography).attrs<{ as?: React.ElementType }>(({ as }) => ({
  $variant: "body-4-medium",
  as: as ?? "a",
}))<{ "aria-current": "location" | undefined }>`
  border: none;
  background-color: transparent;

  border-radius: 0.25rem;
  transition: all 0.3s ease;

  color: ${({ theme }) => theme.colors.grey.$500};

  text-decoration: none;

  a&,
  button& {
    color: ${({ theme }) => theme.colors.grey.$600};
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 0.25rem;
    text-decoration-thickness: 0.125rem;
    text-decoration-color: ${({ theme }) => theme.colors.grey.$300};

    :hover {
      text-decoration-color: ${({ theme }) => theme.colors.black.$900};
    }

    :focus {
      outline: 4px solid ${({ theme }) => theme.colors.grey.$400};
    }

    :active {
      text-decoration-color: transparent;
    }
  }
`

export type DeprecatedBreadcrumbProps<C extends StyledTarget<"web">> = PolymorphicComponentProps<
  "web",
  { children: [ReactElement, ReactElement] },
  C,
  C
>

export const DeprecatedBreadcrumb = <C extends StyledTarget<"web">>({
  children,
  ...props
}: DeprecatedBreadcrumbProps<C>) => {
  const nbOfItems = Children.count(children)

  return (
    <Nav aria-label="breadcrumbs" {...props}>
      <NavList>
        {Children.map(children, (child, i) => (
          <NavItem>
            {child}
            {i + 1 !== nbOfItems ? <MarginedChevron aria-hidden="true" /> : null}
          </NavItem>
        ))}
      </NavList>
    </Nav>
  )
}
